import { useState } from 'react';
import { optimize } from '../lib/imageOptimizer';

/**
 * Smart product image with:
 *  - WebP optimization via wsrv.nl CDN (auto resize + compress)
 *  - Animated skeleton shimmer while loading
 *  - Fallback placeholder if image fails (404, CORS, broken URL)
 *  - Native lazy loading
 *
 * Drop-in replacement for <img> wherever a product photo is shown.
 *
 * @param {object} props
 * @param {string} props.src        Original (Supabase or external) image URL
 * @param {string} props.alt
 * @param {number} [props.width]    Target render width in CSS px (default 320)
 * @param {boolean} [props.eager]   Disables lazy loading & enables fetchpriority="high"
 * @param {string} [props.className] Extra classes on the <img>
 */
export default function ProductImg({ src, alt = '', width = 320, eager = false, className = '' }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // 1×1 transparent SVG fallback (encoded inline, no extra request)
  const FALLBACK =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 400">
         <rect width="100%" height="100%" fill="#f4f4f5"/>
         <g fill="#a1a1aa" font-family="system-ui,sans-serif" text-anchor="middle">
           <text x="160" y="190" font-size="14" font-weight="600">Image indisponible</text>
           <text x="160" y="215" font-size="11">One Maillot</text>
         </g>
       </svg>`
    );

  const finalSrc = errored ? FALLBACK : optimize(src, width);

  return (
    <span className="relative block w-full h-full overflow-hidden bg-zinc-100">
      {/* Skeleton shimmer — visible until the image actually paints */}
      {!loaded && !errored && (
        <span
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-r from-zinc-100 via-zinc-200 to-zinc-100"
        />
      )}
      <img
        src={finalSrc}
        alt={alt}
        referrerPolicy="no-referrer"
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        {...(eager ? { fetchpriority: 'high' } : {})}
        onLoad={() => setLoaded(true)}
        onError={() => { setErrored(true); setLoaded(true); }}
        className={`relative w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      />
    </span>
  );
}
